import type { CSSProperties } from 'react';
import { styleBoutonPrincipal, styleBoutonSecondaire } from '../../../../../packages/ui/src/composants/styles.js';
import { COULEURS, ESPACEMENTS, styleBase, styleParagraphe } from '../../../../../packages/ui/src/tokens/tokens.js';
import type { EtatPhoto } from './controleur-photo.js';

export interface PropsEcranPhotoPiece {
  etat: EtatPhoto;
  onChoisir: (fichier: File) => void;
  onEnvoyer: () => void;
  onReprendre: () => void;
  /** La pièce demandée, telle que le cabinet l'a libellée pour l'héritier (portail.accueil). */
  libellePiece?: string;
  /** Titre de l'écran (celui du contrat quand l'écran est branché dans la coquille). */
  titreEcran?: string;
}

const VARIANTE = 'client' as const;

const styleCadre: CSSProperties = {
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: 360,
  aspectRatio: '1 / 1.4142',
  border: `3px dashed ${COULEURS.marine}`,
  borderRadius: 12,
  backgroundColor: COULEURS.fondDoux,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: ESPACEMENTS.m,
  textAlign: 'center',
};

/** M05 : photo d'une pièce par la famille. Une seule chose à faire à la fois, des mots simples, des cibles de 48 px. */
export function EcranPhotoPiece({
  etat,
  onChoisir,
  onEnvoyer,
  onReprendre,
  libellePiece,
  titreEcran = 'Envoyer une pièce',
}: PropsEcranPhotoPiece) {
  const { phase } = etat;
  const paragraphe = styleParagraphe(VARIANTE);
  return (
    <section
      style={{
        ...styleBase(VARIANTE),
        display: 'flex',
        flexDirection: 'column',
        gap: ESPACEMENTS.m,
        padding: ESPACEMENTS.m,
        maxWidth: 480,
      }}
    >
      <h1 style={{ ...styleBase(VARIANTE), fontSize: 24, margin: 0 }}>{titreEcran}</h1>
      {libellePiece ? <p style={{ ...paragraphe, fontWeight: 600 }}>{libellePiece}</p> : null}

      {phase === 'attente' || phase === 'preparation' ? (
        <>
          <p style={paragraphe}>Placez le document dans le cadre, bien à plat et bien éclairé, puis prenez la photo.</p>
          <div data-cadre="" aria-hidden="true" style={styleCadre}>
            <span style={paragraphe}>Le document entier doit rester dans ce cadre.</span>
          </div>
          <label
            style={{
              ...styleBoutonPrincipal(VARIANTE),
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            Prendre la photo
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                if (fichier) onChoisir(fichier);
              }}
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}
            />
          </label>
          {phase === 'preparation' ? (
            <p role="status" style={paragraphe}>
              Préparation de la photo en cours.
            </p>
          ) : null}
        </>
      ) : null}

      {phase === 'apercu' ? (
        <>
          <p style={paragraphe}>Voici votre photo. Si elle est lisible, envoyez-la ; sinon, reprenez-la.</p>
          {etat.apercuUrl ? (
            <img
              src={etat.apercuUrl}
              alt="Aperçu de votre photo"
              style={{ width: '100%', maxWidth: 360, height: 'auto', border: `1px solid ${COULEURS.bordure}`, borderRadius: 8 }}
            />
          ) : null}
          <p style={{ ...paragraphe, color: COULEURS.texteSecondaire }}>{etat.resume}</p>
          <button type="button" onClick={onEnvoyer} style={styleBoutonPrincipal(VARIANTE)}>
            Envoyer
          </button>
          <button type="button" onClick={onReprendre} style={styleBoutonSecondaire(VARIANTE)}>
            Reprendre
          </button>
        </>
      ) : null}

      {phase === 'envoi' ? (
        <p role="status" aria-live="polite" style={paragraphe}>{`Envoi en cours : ${Math.round((etat.progres ?? 0) * 100)} %`}</p>
      ) : null}

      {phase === 'attente_reseau' || phase === 'termine' ? (
        <p role="status" aria-live="polite" style={paragraphe}>
          {etat.message}
        </p>
      ) : null}

      {phase === 'erreur' ? (
        <>
          <p role="alert" style={{ ...paragraphe, color: COULEURS.erreur }}>
            {etat.message}
          </p>
          <button type="button" onClick={onReprendre} style={styleBoutonSecondaire(VARIANTE)}>
            Reprendre
          </button>
        </>
      ) : null}
    </section>
  );
}
